import { useQuery } from "@tanstack/react-query"
import { client, APIError } from "@/api"

interface SubscriptionsQueryParams {
  page?: number
  pageSize?: number
  status?: string
  has_team?: boolean
  plan_id?: string
}

export interface Subscription {
  id: number
  creator_id: number
  entity_id: number | null
  entity_type: string
  organization_public_id: string | null
  organization_name: string | null
  plan_id: string
  plan_version_id: number | null
  stripe_subscription_id: string | null
  stripe_customer_id: string | null
  status: "trialing" | "active" | "cancelled" | "past_due" | "unpaid"
  current_period_start: string | null
  current_period_end: string | null
  trial_end_date: string | null
  cancel_at_period_end: boolean | null
  cancellation_reason: string | null
  cancelled_at: string | null
  /** Non-null means this is a comp: a real Stripe subscription at 100% off. */
  comp_reason: string | null
  created_at: string
  updated_at: string
  creator: {
    public_id: string
    name: string | null
    email: string
  } | null
  team: {
    public_id: string
    name: string
  } | null
  plan: {
    id: string
    name: string
    tier: string
  } | null
  team_subscription_status: string | null
}

interface SubscriptionsResponse {
  ok: boolean
  subscriptions: Subscription[]
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

export function useSubscriptions(params: SubscriptionsQueryParams = {}) {
  const { page = 1, pageSize = 50, status, has_team, plan_id } = params

  return useQuery<SubscriptionsResponse>({
    queryKey: ["admin", "subscriptions", page, pageSize, status, has_team, plan_id],
    queryFn: async () => {
      const response = await client.admin.subscriptions.$get({
        query: {
          page: page.toString(),
          pageSize: pageSize.toString(),
          status: status || undefined,
          has_team: has_team !== undefined ? has_team.toString() : undefined,
          plan_id: plan_id || undefined,
        },
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        let errorCode = "SUBSCRIPTIONS_FETCH_FAILED"

        try {
          const errorData = (await response.clone().json()) as { message?: string; code?: string }
          errorMessage = errorData.message || errorMessage
          errorCode = errorData.code || errorCode
        } catch {
          // Fallback to status text if JSON parsing fails
          errorMessage = response.statusText || errorMessage
        }

        throw new APIError(response.status, errorCode, new Error(errorMessage))
      }

      return response.json()
    },
  })
}
